import { type Page, type SavedView, savedViewCreateSchema } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  Dialog,
  DialogCancel,
  Input,
  Menu,
  Select,
  useToast,
} from '@fruuit/ui'
import { Bookmark, MoreHorizontal } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { api, apiFetch } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'
import { type FieldErrors, serverErrors, validate } from './forms'
import { keys } from './queries'

/**
 * Saved engagement views (PORT-006..012, GAP-UI-007): private to their owner, with rename
 * and delete, and an indicator of the view currently applied. Engagement filters map onto
 * the shared saved-view filter keys: status → engagement_status, search → query, theme.
 */
export interface EngagementFilterValues {
  status?: string
  theme?: string
  q?: string
}

export function toViewFilters(f: EngagementFilterValues): Record<string, string> {
  const out: Record<string, string> = {}
  if (f.status) out.engagement_status = f.status
  if (f.theme) out.theme = f.theme
  if (f.q) out.query = f.q
  return out
}

export function fromViewFilters(filters: Record<string, string>): EngagementFilterValues {
  return {
    ...(filters.engagement_status ? { status: filters.engagement_status } : {}),
    ...(filters.theme ? { theme: filters.theme } : {}),
    ...(filters.query ? { q: filters.query } : {}),
  }
}

function sameFilters(a: Record<string, string>, b: Record<string, string>): boolean {
  const ka = Object.keys(a).sort()
  const kb = Object.keys(b).sort()
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k])
}

export function SavedViews({
  current,
  onApply,
}: {
  current: EngagementFilterValues
  onApply: (filters: EngagementFilterValues) => void
}) {
  const views = useApiQuery<Page<SavedView>>([...keys.savedViews, 'engagements'], '/saved-views', {
    query: { scope: 'engagements', limit: 50 },
  })
  const [dialog, setDialog] = useState<
    | { mode: 'save' }
    | { mode: 'rename'; view: SavedView }
    | { mode: 'delete'; view: SavedView }
    | null
  >(null)
  const items = views.data?.items ?? []
  const filters = toViewFilters(current)
  const applied = items.find((v) => sameFilters(v.filters, filters)) ?? null

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Select
        label="Saved views"
        hideLabel
        className="w-full sm:w-56"
        placeholder={items.length === 0 ? 'No saved views yet' : 'Saved views'}
        options={items.map((v) => ({ value: v.id, label: v.name }))}
        value={applied?.id ?? ''}
        onChange={(event) => {
          const view = items.find((v) => v.id === event.target.value)
          onApply(view ? fromViewFilters(view.filters) : {})
        }}
      />
      <Button
        variant="secondary"
        icon={<Bookmark />}
        onClick={() => setDialog({ mode: 'save' })}
        disabled={Object.keys(filters).length === 0 || applied !== null}
      >
        Save view
      </Button>
      {applied ? (
        <>
          <Badge tone="brand">Applied: {applied.name}</Badge>
          <Menu
            trigger={(props) => (
              <Button variant="ghost" size="sm" aria-label={`Manage ${applied.name}`} {...props}>
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            )}
            items={[
              {
                id: 'rename',
                label: 'Rename view',
                onSelect: () => setDialog({ mode: 'rename', view: applied }),
              },
              {
                id: 'delete',
                label: 'Delete view',
                tone: 'danger',
                onSelect: () => setDialog({ mode: 'delete', view: applied }),
              },
            ]}
          />
        </>
      ) : null}
      {dialog?.mode === 'save' ? (
        <NameDialog filters={filters} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.mode === 'rename' ? (
        <NameDialog filters={filters} view={dialog.view} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.mode === 'delete' ? (
        <DeleteViewDialog view={dialog.view} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  )
}

function NameDialog({
  filters,
  view,
  onClose,
}: {
  filters: Record<string, string>
  view?: SavedView
  onClose: () => void
}) {
  const [name, setName] = useState(view?.name ?? '')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()
  const mutation = useApiMutation(
    (input: Record<string, unknown>, { requestId }) =>
      view
        ? api.patch(`/saved-views/${view.id}`, input, { requestId })
        : api.post('/saved-views', input, { requestId }),
    {
      invalidate: [keys.savedViews],
      onSuccess: () => {
        onClose()
        toast({ tone: 'success', message: view ? 'View renamed.' : 'View saved.' })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const message = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const checked = validate(savedViewCreateSchema, { name, scope: 'engagements', filters })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    mutation.mutate(
      view ? { revision: view.revision, name: checked.data.name } : { ...checked.data },
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={view ? 'Rename view' : 'Save this view'}
      description="Saved views are private to you."
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="saved-view-form" loading={mutation.isPending}>
            {view ? 'Rename' : 'Save view'}
          </Button>
        </>
      }
    >
      <form id="saved-view-form" onSubmit={submit} noValidate className="space-y-4">
        <Input
          label="Name"
          required
          maxLength={80}
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={errors.name}
        />
        {message ? (
          <Callout tone="danger" role="alert">
            {message}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}

function DeleteViewDialog({ view, onClose }: { view: SavedView; onClose: () => void }) {
  const { toast } = useToast()
  const mutation = useApiMutation(
    (input: { revision: number }, { requestId }) =>
      apiFetch(`/saved-views/${view.id}`, { method: 'DELETE', body: input, requestId }),
    {
      invalidate: [keys.savedViews],
      onSuccess: () => {
        onClose()
        toast({ tone: 'success', message: 'View deleted.' })
      },
    },
  )
  const message = mutation.error ? serverErrors(mutation.error).message : null
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={`Delete "${view.name}"?`}
      footer={
        <>
          <DialogCancel />
          <Button
            variant="danger"
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ revision: view.revision })}
          >
            Delete view
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">
        Your filters stay as they are; only the saved view goes.
      </p>
      {message ? (
        <Callout tone="danger" role="alert" className="mt-3">
          {message}
        </Callout>
      ) : null}
    </Dialog>
  )
}
